import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config';

const ProjectObjectives = () => {
  const { projectId } = useParams();
  const [loading, setLoading] = useState(true);
  const [objectives, setObjectives] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    nom: '',
    description: '',
    type: 'ca',
    valeur_cible: '',
    valeur_actuelle: '',
    unite: 'XOF',
    date_debut: '',
    date_fin: '',
    statut: 'en_cours'
  });

  const fetchObjectives = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/project-finances/${projectId}/objectives`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setObjectives(res.data.objectives || []);
      setCanManage(res.data.can_manage || false);
    } catch (err) {
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchObjectives();
  }, [fetchObjectives]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      if (editingId) {
        await axios.put(
          `${API_URL}/project-finances/${projectId}/objectives/${editingId}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${API_URL}/project-finances/${projectId}/objectives`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      setShowModal(false);
      resetForm();
      fetchObjectives();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  const handleDelete = async (objectiveId) => {
    if (!window.confirm('Supprimer cet objectif ?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_URL}/project-finances/${projectId}/objectives/${objectiveId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchObjectives();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  const handleEdit = (objective) => {
    setFormData({
      nom: objective.nom,
      description: objective.description || '',
      type: objective.type,
      valeur_cible: objective.valeur_cible,
      valeur_actuelle: objective.valeur_actuelle || '',
      unite: objective.unite || 'XOF',
      date_debut: objective.date_debut || '',
      date_fin: objective.date_fin || '',
      statut: objective.statut || 'en_cours'
    });
    setEditingId(objective.id);
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      nom: '',
      description: '',
      type: 'ca',
      valeur_cible: '',
      valeur_actuelle: '',
      unite: 'XOF',
      date_debut: '',
      date_fin: '',
      statut: 'en_cours'
    });
    setEditingId(null);
  };

  const formatMoney = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('fr-FR');
  };

  const typeLabels = {
    ca: { label: 'Chiffre d\'affaires', color: 'bg-green-100 text-green-700', icon: '💵' },
    benefice: { label: 'Bénéfice', color: 'bg-emerald-100 text-emerald-700', icon: '📈' },
    clients: { label: 'Clients', color: 'bg-blue-100 text-blue-700', icon: '👥' },
    ventes: { label: 'Ventes', color: 'bg-purple-100 text-purple-700', icon: '🛒' },
    autre: { label: 'Autre', color: 'bg-gray-100 text-gray-700', icon: '📋' }
  };

  const statusLabels = {
    en_cours: { label: 'En cours', color: 'bg-blue-100 text-blue-700', icon: '⏳' },
    atteint: { label: 'Atteint', color: 'bg-green-100 text-green-700', icon: '✅' },
    non_atteint: { label: 'Non atteint', color: 'bg-red-100 text-red-700', icon: '❌' },
    abandonne: { label: 'Abandonné', color: 'bg-gray-100 text-gray-700', icon: '🚫' }
  };

  const getProgress = (objective) => {
    const current = parseFloat(objective.valeur_actuelle || 0);
    const target = parseFloat(objective.valeur_cible || 1);
    return Math.min(100, Math.round((current / target) * 100));
  };

  const getProgressColor = (progress) => {
    if (progress >= 100) return 'bg-green-500';
    if (progress >= 75) return 'bg-blue-500';
    if (progress >= 50) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 text-white">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <Link to={`/projects/${projectId}/finances`} className="text-indigo-200 hover:text-white text-sm mb-2 inline-flex items-center gap-1">
            ← Retour au dashboard
          </Link>
          <h1 className="text-3xl font-bold">🎯 Objectifs financiers</h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Navigation Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          <Link to={`/projects/${projectId}/finances`} className="px-4 py-2 bg-white text-gray-700 rounded-xl font-medium hover:bg-gray-100 shadow-sm">
            📊 Dashboard
          </Link>
          <Link to={`/projects/${projectId}/finances/collaborators`} className="px-4 py-2 bg-white text-gray-700 rounded-xl font-medium hover:bg-gray-100 shadow-sm">
            👥 Collaborateurs
          </Link>
          <Link to={`/projects/${projectId}/finances/payments`} className="px-4 py-2 bg-white text-gray-700 rounded-xl font-medium hover:bg-gray-100 shadow-sm">
            💳 Paiements
          </Link>
          <Link to={`/projects/${projectId}/finances/objectives`} className="px-4 py-2 bg-indigo-600 text-white rounded-xl font-medium">
            🎯 Objectifs
          </Link>
          <Link to={`/projects/${projectId}/finances/reports`} className="px-4 py-2 bg-white text-gray-700 rounded-xl font-medium hover:bg-gray-100 shadow-sm">
            📈 Bilans
          </Link>
        </div>

        {/* Actions */}
        {canManage && (
          <div className="mb-6">
            <button
              onClick={() => { resetForm(); setShowModal(true); }}
              className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg"
            >
              + Nouvel objectif
            </button>
          </div>
        )}

        {/* Loading */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-500">Chargement...</p>
          </div>
        ) : objectives.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
            <span className="text-6xl mb-4 block">🎯</span>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Aucun objectif</h3>
            <p className="text-gray-500">Définissez des objectifs financiers pour suivre vos ambitions</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {objectives.map((objective) => {
              const progress = getProgress(objective);
              const progressColor = getProgressColor(progress);
              const typeInfo = typeLabels[objective.type] || typeLabels.autre;
              const statusInfo = statusLabels[objective.statut] || statusLabels.en_cours;
              
              return (
                <div key={objective.id} className="bg-white rounded-2xl shadow-sm overflow-hidden">
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <span className="text-3xl">{typeInfo.icon}</span>
                        <div>
                          <h3 className="font-bold text-gray-900 text-lg">{objective.nom}</h3>
                          <div className="flex gap-2 mt-1">
                            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${typeInfo.color}`}>
                              {typeInfo.label}
                            </span>
                            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
                              {statusInfo.icon} {statusInfo.label}
                            </span>
                          </div>
                        </div>
                      </div>
                      {canManage && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(objective)}
                            className="p-2 text-gray-400 hover:text-indigo-600 transition-colors"
                            title="Modifier"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => handleDelete(objective.id)}
                            className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                            title="Supprimer"
                          >
                            🗑️
                          </button>
                        </div>
                      )}
                    </div>
                    
                    {objective.description && (
                      <p className="text-gray-600 text-sm mb-4">{objective.description}</p>
                    )}
                    
                    {/* Progress */}
                    <div className="mb-4">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-500">Progression</span>
                        <span className="font-bold text-gray-800">{progress}%</span>
                      </div>
                      <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${progressColor} transition-all duration-500`}
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    </div>
                    
                    {/* Amounts */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-gray-50 rounded-xl p-3">
                        <p className="text-xs text-gray-500 mb-1">Actuel</p>
                        <p className="font-bold text-gray-800">
                          {objective.unite === 'XOF' || !objective.unite ? formatMoney(objective.valeur_actuelle) : `${objective.valeur_actuelle || 0} ${objective.unite}`}
                        </p>
                      </div>
                      <div className="bg-indigo-50 rounded-xl p-3">
                        <p className="text-xs text-indigo-500 mb-1">Objectif</p>
                        <p className="font-bold text-indigo-600">
                          {objective.unite === 'XOF' || !objective.unite ? formatMoney(objective.valeur_cible) : `${objective.valeur_cible} ${objective.unite}`}
                        </p>
                      </div>
                    </div>
                    
                    {/* Date limite */}
                    {objective.date_fin && (
                      <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                        <span className="text-sm text-gray-500">📅 Échéance</span>
                        <span className={`text-sm font-medium ${new Date(objective.date_fin) < new Date() ? 'text-red-600' : 'text-gray-800'}`}>
                          {formatDate(objective.date_fin)}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {/* Status bar */}
                  <div className={`h-1 ${progressColor}`} />
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-xl font-bold text-gray-900">
                {editingId ? '✏️ Modifier l\'objectif' : '🎯 Nouvel objectif'}
              </h2>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                <input
                  type="text"
                  value={formData.nom}
                  onChange={(e) => setFormData(prev => ({ ...prev, nom: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
                  placeholder="Ex: Atteindre 1M XOF de CA"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
                  rows="2"
                  placeholder="Description de l'objectif..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Type d'objectif</label>
                <div className="grid grid-cols-3 gap-2">
                  {Object.entries(typeLabels).map(([key, val]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, type: key }))}
                      className={`p-2 rounded-xl border-2 text-center transition-all ${
                        formData.type === key 
                          ? 'border-indigo-500 bg-indigo-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <span className="text-lg">{val.icon}</span>
                      <p className="font-medium text-xs mt-1">{val.label}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Unité de mesure */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Unité de mesure</label>
                <select
                  value={formData.unite}
                  onChange={(e) => setFormData(prev => ({ ...prev, unite: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="XOF">💰 XOF (Franc CFA)</option>
                  <option value="unités">📦 Unités</option>
                  <option value="clients">👥 Clients</option>
                  <option value="%">📊 Pourcentage (%)</option>
                </select>
              </div>

              {/* Valeurs */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Valeur cible *</label>
                  <div className="relative">
                    <input
                      type="number"
                      step="1"
                      min="0"
                      value={formData.valeur_cible}
                      onChange={(e) => setFormData(prev => ({ ...prev, valeur_cible: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 font-semibold"
                      placeholder="0"
                      required
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{formData.unite}</span>
                  </div>
                </div>
                {editingId && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Valeur actuelle</label>
                    <div className="relative">
                      <input
                        type="number"
                        step="1"
                        min="0"
                        value={formData.valeur_actuelle}
                        onChange={(e) => setFormData(prev => ({ ...prev, valeur_actuelle: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 font-semibold"
                        placeholder="0"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{formData.unite}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Statut - seulement en modification */}
              {editingId && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Statut</label>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(statusLabels).map(([key, val]) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, statut: key }))}
                        className={`p-2 rounded-xl border-2 text-center transition-all ${
                          formData.statut === key 
                            ? 'border-indigo-500 bg-indigo-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <span className="text-lg">{val.icon}</span>
                        <p className="font-medium text-xs mt-1">{val.label}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date début</label>
                  <input
                    type="date"
                    value={formData.date_debut}
                    onChange={(e) => setFormData(prev => ({ ...prev, date_debut: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date fin (échéance)</label>
                  <input
                    type="date"
                    value={formData.date_fin}
                    onChange={(e) => setFormData(prev => ({ ...prev, date_fin: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-3 border border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-medium hover:from-indigo-700 hover:to-purple-700 transition-all"
                >
                  {editingId ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectObjectives;
